import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import {
  MdCheck,
  MdKeyboardArrowDown,
  MdSearch
} from 'react-icons/md';

const FIELD_BG = '#414141';
const POPOVER_BG = '#2A2A2A';

/**
 * SearchableSelect
 *
 * Drop-in replacement for a native <Select> with:
 *   - Type-to-filter input
 *   - Two-line option layout (primary `label` + secondary `description`)
 *   - Synthetic onChange event ({ target: { name, value } }) so it plugs into
 *     existing form handlers used by DynamicFormField / DynamicInputModal.
 *
 * Option shape: { value, label, description?, isDisabled? }
 */
const SearchableSelect = ({
  name,
  options = [],
  value,
  placeholder = 'Select an option',
  onChange,
  isDisabled = false,
  emptyMessage = 'No options found'
}) => {
  const [search, setSearch] = useState('');
  const searchInputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => {
      const label = (option.label ?? '').toString().toLowerCase();
      const description = (option.description ?? '').toString().toLowerCase();
      const optionValue = (option.value ?? '').toString().toLowerCase();
      return (
        label.includes(query) ||
        description.includes(query) ||
        optionValue.includes(query)
      );
    });
  }, [options, search]);

  const handleSelect = (option) => {
    if (option.isDisabled) return;
    onChange?.({ target: { name, value: option.value } });
    setSearch('');
    onClose();
  };

  const handleOpen = () => {
    if (isDisabled) return;
    setSearch('');
    onOpen();
  };

  const renderTriggerContent = () => {
    if (selectedOption) {
      return (
        <Box flex="1" minW={0} textAlign="left">
          <Text
            color="white"
            fontSize="sm"
            fontWeight="500"
            lineHeight="1.3"
            isTruncated
          >
            {selectedOption.label}
          </Text>
          {selectedOption.description && (
            <Text
              color="whiteAlpha.600"
              fontSize="xs"
              lineHeight="1.3"
              mt={0.5}
              isTruncated
            >
              {selectedOption.description}
            </Text>
          )}
        </Box>
      );
    }
    return (
      <Text
        color="gray.500"
        fontSize="sm"
        lineHeight="1.3"
        flex="1"
        textAlign="left"
        isTruncated
      >
        {placeholder}
      </Text>
    );
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      onOpen={handleOpen}
      placement="bottom-start"
      matchWidth
      initialFocusRef={searchInputRef}
      autoFocus={false}
      isLazy
      gutter={4}
    >
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          disabled={isDisabled}
          w="100%"
          minH={selectedOption?.description ? '3.25rem' : '2.75rem'}
          py={3}
          pl="12px"
          pr="10px"
          bg={FIELD_BG}
          border="0"
          borderRadius="12px"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          textAlign="left"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          opacity={isDisabled ? 0.6 : 1}
          _hover={{ bg: isDisabled ? FIELD_BG : '#4a4a4a' }}
          _focusVisible={{
            outline: 'none',
            boxShadow: '0 0 0 2px var(--chakra-colors-brand-400)'
          }}
        >
          <Flex w="100%" align="center" gap={3}>
            {renderTriggerContent()}
            <Icon
              as={MdKeyboardArrowDown}
              boxSize={5}
              color="whiteAlpha.700"
              flexShrink={0}
            />
          </Flex>
        </Box>
      </PopoverTrigger>
      <PopoverContent
        bg={POPOVER_BG}
        borderColor="whiteAlpha.300"
        borderWidth="1px"
        borderRadius="lg"
        boxShadow="lg"
        _focus={{ outline: 'none', boxShadow: 'lg' }}
        w="100%"
      >
        <PopoverBody py={2} px={0}>
          <Box px="4px" mb={2}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="whiteAlpha.700" />
              </InputLeftElement>
              <Input
                ref={searchInputRef}
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                bg={FIELD_BG}
                color="white"
                borderColor="transparent"
                _hover={{ bg: FIELD_BG }}
                _focus={{ bg: FIELD_BG, borderColor: 'brand.400' }}
                borderRadius="md"
                autoComplete="off"
              />
            </InputGroup>
          </Box>
          <Box maxH="240px" overflowY="auto" className="scroll-md">
            {filteredOptions.length === 0 ? (
              <Box py={4} textAlign="center">
                <Text color="whiteAlpha.600" fontSize="sm">
                  {emptyMessage}
                </Text>
              </Box>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;
                return (
                  <Flex
                    key={option.value}
                    align="center"
                    justify="space-between"
                    pl="12px"
                    pr="10px"
                    py={2}
                    borderRadius="md"
                    cursor={option.isDisabled ? 'not-allowed' : 'pointer'}
                    opacity={option.isDisabled ? 0.5 : 1}
                    bg={isSelected ? 'whiteAlpha.200' : 'transparent'}
                    _hover={{
                      bg: option.isDisabled
                        ? 'transparent'
                        : 'whiteAlpha.300'
                    }}
                    onClick={() => handleSelect(option)}
                  >
                    <Box minW={0} flex="1">
                      <Text
                        color="white"
                        fontSize="sm"
                        fontWeight="500"
                        lineHeight="1.3"
                        isTruncated
                      >
                        {option.label}
                      </Text>
                      {option.description && (
                        <Text
                          color="whiteAlpha.600"
                          fontSize="xs"
                          lineHeight="1.3"
                          mt={0.5}
                          isTruncated
                        >
                          {option.description}
                        </Text>
                      )}
                    </Box>
                    {isSelected && (
                      <Icon
                        as={MdCheck}
                        boxSize={4}
                        color="brand.400"
                        flexShrink={0}
                        ml={2}
                      />
                    )}
                  </Flex>
                );
              })
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
